import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useEditor, useToasts, useValue } from "tldraw";
import type { AgentAction } from "../../shared/types/AgentAction";
import type { Streaming } from "../../shared/types/Streaming";
import { TldrawAgent } from "./TldrawAgent";
import { TldrawAgentApp } from "./TldrawAgentApp";
import { WebSocketTransport, NoOpTransport } from "./transport";
import { consumeEagerTransport } from "./eagerTransport";
import { CANVAS_GATEWAY_CONFIGURED } from "../../flags";
import { useNotepadStore } from "../../features/notepad/store/useNotepadStore";

const TldrawAgentAppContext = createContext<TldrawAgentApp | null>(null);

export interface TldrawAgentAppProviderProps {
  children?: ReactNode;
  /**
   * Callback fired when the app is created. Use this to pass the app
   * to components outside the Tldraw component via TldrawAgentAppContextProvider.
   */
  onMount?: (app: TldrawAgentApp) => void;
  /**
   * Callback fired when the app is disposed.
   */
  onUnmount?: () => void;
  /** When false, skip localStorage load/save. Default: true. */
  persist?: boolean;
  /** Called when an LLM stream completes successfully. */
  onStreamComplete?: () => void;
  /** Called when an error occurs (WS disconnect, LLM error, action error). */
  onError?: (error: Error) => void;
  /** Called after each action is executed on the canvas. */
  onActionExecuted?: (action: Streaming<AgentAction>) => void;
  /** Called when a malformed action crashes the editor and diff rollback also fails. */
  onActionCrash?: () => void;
}

/**
 * Provider component that creates and manages a TldrawAgentApp instance for the current editor.
 *
 * This component should be rendered inside a `<Tldraw>` component to have access to the editor context.
 * It creates a TldrawAgentApp instance, provides it via context, and cleans up on unmount.
 *
 * The provider waits until the app and agent exist before rendering children,
 * ensuring useAgent() always returns a valid agent.
 *
 * For components defined in Tldraw's `components` prop, use `onMount` to get the app instance
 * and wrap them with `TldrawAgentAppContextProvider`.
 *
 * @example
 * ```tsx
 * function App() {
 *   const [app, setApp] = useState<TldrawAgentApp | null>(null)
 *
 *   const components = useMemo(() => ({
 *     InFrontOfTheCanvas: () => app && (
 *       <TldrawAgentAppContextProvider app={app}>
 *         <AgentHighlights />
 *       </TldrawAgentAppContextProvider>
 *     ),
 *   }), [app])
 *
 *   return (
 *     <Tldraw components={components}>
 *       <TldrawAgentAppProvider onMount={setApp} onUnmount={() => setApp(null)} />
 *     </Tldraw>
 *   )
 * }
 * ```
 */
export const TldrawAgentAppProvider = memo(function TldrawAgentAppProvider({
  children,
  onMount,
  onUnmount,
  persist = true,
  onStreamComplete,
  onError: onErrorProp,
  onActionExecuted,
  onActionCrash,
}: TldrawAgentAppProviderProps) {
  const editor = useEditor();
  const toasts = useToasts();
  const [app, setApp] = useState<TldrawAgentApp | null>(null);

  // Error handler for agent errors
  const handleError = useCallback(
    (e: any) => {
      const message =
        typeof e === "string" ? e : e instanceof Error && e.message;
      toasts.addToast({
        title: "Error",
        description: message || "An error occurred",
        severity: "error",
      });
      console.error(e);
      onErrorProp?.(
        e instanceof Error ? e : new Error(message || "An error occurred"),
      );
    },
    [toasts, onErrorProp],
  );

  // Read callbacks through a ref so a changed callback identity does not re-run
  // the create-effect below — that would dispose the app and its WebSocket,
  // whose onclose reconnects, causing a tight open/dispose/reconnect loop.
  const callbacksRef = useRef({
    handleError,
    onMount,
    onUnmount,
    persist,
    onStreamComplete,
    onActionExecuted,
    onActionCrash,
  });
  callbacksRef.current = {
    handleError,
    onMount,
    onUnmount,
    persist,
    onStreamComplete,
    onActionExecuted,
    onActionCrash,
  };

  // Create the TldrawAgentApp instance. Depends only on `editor` so the
  // WebSocket transport is not torn down and recreated on unrelated re-renders.
  useEffect(() => {
    const cbs = callbacksRef.current;
    // In student review mode, use NoOpTransport — no gateway WS connection.
    const isStudentReview =
      useNotepadStore.getState().ingressType === "student";
    // Pass ref-reading wrappers unconditionally: the callbacks are only ever
    // invoked via optional-chaining downstream, so their presence here never
    // changes behavior, and reading through the ref means a callback that
    // becomes defined after this mount-time snapshot still gets wired.
    const instance = new TldrawAgentApp(editor, {
      onError: (e) => callbacksRef.current.handleError(e),
      transportFactory:
        CANVAS_GATEWAY_CONFIGURED && !isStudentReview
          ? () => consumeEagerTransport() ?? new WebSocketTransport()
          : () => new NoOpTransport(),
      onStreamComplete: () => callbacksRef.current.onStreamComplete?.(),
      onActionExecuted: (action) =>
        callbacksRef.current.onActionExecuted?.(action),
      onActionCrash: () => callbacksRef.current.onActionCrash?.(),
    });

    // Load persisted state first (this will create agents from persisted data)
    if (cbs.persist) {
      instance.persistence.loadState();
    }

    // Ensure at least one agent exists (creates one if none were loaded)
    instance.agents.ensureAtLeastOneAgent();

    // Start auto-saving (must be after loadState to avoid saving during load)
    if (cbs.persist) {
      instance.persistence.startAutoSave();
    }

    setApp(instance);

    // Notify parent
    callbacksRef.current.onMount?.(instance);

    return () => {
      // Cancel in-progress streams before dispose, but skip agent access if
      // the app was already disposed by editor lifecycle events.
      if (!instance.isDisposed) {
        const agents = instance.agents.getAgents();
        for (const agent of agents) {
          agent.cancel();
        }
      }
      instance.dispose();
      setApp(null);
      callbacksRef.current.onUnmount?.();
    };
  }, [editor]);

  // Don't render children until app exists
  if (!app) {
    return null;
  }

  return (
    <TldrawAgentAppContext.Provider value={app}>
      {children}
    </TldrawAgentAppContext.Provider>
  );
});

/**
 * Context provider that wraps children with the TldrawAgentApp context.
 * Use this to provide the app context to components defined in Tldraw's `components` prop.
 *
 * @example
 * ```tsx
 * const components = useMemo(() => ({
 *   InFrontOfTheCanvas: () => app && (
 *     <TldrawAgentAppContextProvider app={app}>
 *       <AgentHighlights />
 *     </TldrawAgentAppContextProvider>
 *   ),
 * }), [app])
 * ```
 */
export function TldrawAgentAppContextProvider({
  app,
  children,
}: {
  app: TldrawAgentApp;
  children: ReactNode;
}) {
  return (
    <TldrawAgentAppContext.Provider value={app}>
      {children}
    </TldrawAgentAppContext.Provider>
  );
}

/**
 * Hook to get the TldrawAgentApp instance from context.
 * Must be used inside a TldrawAgentAppProvider or TldrawAgentAppContextProvider.
 *
 * @throws Error if called outside of a provider
 * @returns The TldrawAgentApp instance (guaranteed non-null)
 *
 * @example
 * ```tsx
 * const app = useTldrawAgentApp()
 * // app is guaranteed to exist here
 * const agent = app.agents.getAgent()
 * ```
 */
export function useTldrawAgentApp(): TldrawAgentApp {
  const app = useContext(TldrawAgentAppContext);
  if (!app) {
    throw new Error(
      "useTldrawAgentApp must be used inside a TldrawAgentAppProvider",
    );
  }
  return app;
}

/**
 * Hook to get the default TldrawAgent instance from the app context.
 * Must be used inside a TldrawAgentAppProvider or TldrawAgentAppContextProvider.
 *
 * @throws Error if called outside of a provider or if agent doesn't exist
 * @returns The default TldrawAgent instance (guaranteed non-null)
 *
 * @example
 * ```tsx
 * function ChatPanel() {
 *   const agent = useAgent()
 *   // agent is guaranteed to exist here
 *   agent.request({ agentMessages: ['Draw a cat'] })
 * }
 * ```
 */
export function useAgent(): TldrawAgent {
  const app = useTldrawAgentApp();
  const agent = useValue(
    "agent",
    () => {
      // Guard: app may be synchronously disposed while this derive is still
      // subscribed (React batches setApp(null), so the derive can fire on a
      // disposed app before ChatPanel unmounts).
      if (app.isDisposed) return undefined;
      return app.agents.getAgent();
    },
    [app],
  );
  if (!agent) {
    // Return undefined safely for any transient frame (disposed or not-yet-initialized).
    // All callers (ChatPanel, CanvasContextControls) guard with if (!agent) return null.
    return undefined as unknown as TldrawAgent;
  }
  return agent;
}

/**
 * Hook to get all TldrawAgent instances from the app context.
 * Returns a reactive array that updates when agents are added or removed.
 * Must be used inside a TldrawAgentAppProvider or TldrawAgentAppContextProvider.
 *
 * @throws Error if called outside of a provider
 * @returns Array of all TldrawAgent instances
 *
 * @example
 * ```tsx
 * function AgentList() {
 *   const agents = useAgents()
 *   return (
 *     <ul>
 *       {agents.map(agent => <li key={agent.id}>{agent.id}</li>)}
 *     </ul>
 *   )
 * }
 * ```
 */
export function useAgents(): TldrawAgent[] {
  const app = useTldrawAgentApp();
  return useValue(
    "agents",
    () => {
      if (app.isDisposed) return [];
      return app.agents.getAgents();
    },
    [app],
  );
}
